import { Stack, Tabs } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { InvoiceSettings } from "./components/InvoiceSettings";
import { InvoicesTable } from "./components/InvoicesTable";
import { PaymentsSettings } from "./components/PaymentsSettings";
import { CancelSubscriptionCard } from "./components/CancelSubscriptionCard";
import { Elements } from "@stripe/react-stripe-js";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { useStripe } from "../../../hooks/stripe";
import { AddonsTab } from "./components/AddonsTab";
import { isFeatureEnabled } from "../../../utils/posthog";
import { SpendTab } from "./components/SpendTab.js";
import { use } from "react";
import { z } from "zod";

/** Shared with the route's validateSearch so the tab list and the URL cannot drift apart */
export const billingTabSchema = z.enum(["spend", "payments", "addons"]).catch("payments");

export const Billing = () => {
  const intl = useIntl();
  const {
    stripe,
    options,
  } = useStripe();

  const stripeRes = use(stripe);

  const navigate = useNavigate({
    from: "/$accountName/admin/billing",
  });
  const { tab } = useSearch({
    from: "/$accountName/admin-layout/admin/billing",
  });

  const isAddonsEnabled = isFeatureEnabled("enable-subscription-addons");

  return (
    <Elements stripe={stripeRes} options={options}>
      <Stack gap={16} h="100%">
        <SimpleBreadcrumbs
          parent={intl.formatMessage({ defaultMessage: "Manage account", id: "navigation.manageAccount" })}
          current={intl.formatMessage({ defaultMessage: "Billing", id: "navigation.billing" })}
          description={intl.formatMessage({ defaultMessage: "Manage usage-based pricing, invoices, and payment settings", id: "text.billingDescription" })}
        />
        <Tabs
          value={tab}
          variant="default"
          onChange={(value) => {
            void navigate({
              search: {
                tab: billingTabSchema.parse(value),
              },
            });
          }}
        >
          <Tabs.List pb="lg" w="100%">
            <Tabs.Tab value="spend">
              <FormattedMessage id="navigation.spendTab" defaultMessage="Spend" />
            </Tabs.Tab>

            <Tabs.Tab value="payments">
              <FormattedMessage id="navigation.billingTab" defaultMessage="Billing" />
            </Tabs.Tab>
            {isAddonsEnabled && (
              <Tabs.Tab value="addons">
                <FormattedMessage id="navigation.addonsTab" defaultMessage="Addons" />
              </Tabs.Tab>
            )}
          </Tabs.List>
          <Tabs.Panel value="spend"><SpendTab /></Tabs.Panel>
          <Tabs.Panel value="payments">
            <Stack gap="lg">
              <PaymentsSettings />
              <InvoiceSettings />
              <CancelSubscriptionCard />
              <InvoicesTable />
            </Stack>
          </Tabs.Panel>
          {isAddonsEnabled && <Tabs.Panel value="addons"><AddonsTab /></Tabs.Panel>}
        </Tabs>
      </Stack>
    </Elements>
  );
};
